import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useMotionPreferences from "@/hooks/useMotionPreferences";
import { DURATION, EASING } from "@/lib/motionSystem";

/**
 * ClickSpark — Spark/confetti effect on click.
 * Inspired by React Bits ClickSpark component.
 */
const SPARK_COLORS = ["#6366f1", "#a855f7", "#06b6d4", "#22c55e", "#f59e0b", "#f8fafc"];

function Spark({ x, y, color, angle, id }) {
  const distance = 40 + Math.random() * 60;
  const size = 3 + Math.random() * 4;
  const endX = x + Math.cos(angle) * distance;
  const endY = y + Math.sin(angle) * distance;

  return (
    <motion.div
      key={id}
      className="fixed pointer-events-none z-[9999] rounded-full"
      style={{ width: size, height: size, backgroundColor: color, left: x, top: y }}
      initial={{ opacity: 1, scale: 1 }}
      animate={{ left: endX, top: endY, opacity: 0, scale: 0 }}
      exit={{ opacity: 0 }}
      transition={{ duration: DURATION.press + Math.random() * 0.18, ease: EASING.emphasize }}
    />
  );
}

export default function ClickSpark({ children, className = "", sparkCount = 10, ...props }) {
  const [sparks, setSparks] = useState([]);
  const { allowDecorativeEffects } = useMotionPreferences();

  const handleClick = useCallback((e) => {
    if (!allowDecorativeEffects) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    const newSparks = Array.from({ length: sparkCount }, (_, i) => ({
      id: `${Date.now()}-${i}`,
      x,
      y,
      color: SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)],
      angle: (Math.PI * 2 * i) / sparkCount + (Math.random() - 0.5) * 0.5,
    }));

    setSparks((prev) => [...prev, ...newSparks]);
    setTimeout(() => {
      setSparks((prev) => prev.filter((s) => !newSparks.find((n) => n.id === s.id)));
    }, 1000);
  }, [allowDecorativeEffects, sparkCount]);

  return (
    <>
      <div className={`inline-block ${className}`} onClick={handleClick} {...props}>
        {children}
      </div>
      <AnimatePresence>
        {sparks.map((spark) => (
          <Spark key={spark.id} {...spark} />
        ))}
      </AnimatePresence>
    </>
  );
}
